import React, { useState } from 'react';

function AdvertRequestForm() {
  const [formData, setFormData] = useState({
    advertisementType: '',
    fullName: '',
    phoneNumber: '',
    email: '',
    targetAudience: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.advertisementType.trim()) {
      newErrors.advertisementType = 'Advertisement type is required';
    }

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.targetAudience.trim()) {
      newErrors.targetAudience = 'Target audience is required';
    }

    return newErrors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Prepare data for API
      const submissionData = {
        ...formData,
        timestamp: new Date().toISOString()
      };

      // Send data to your backend API
      const response = await fetch('/api/advert-requests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit request');
      }

      const result = await response.json();
      
      // Show success overlay instead of inline message
      setShowSuccessOverlay(true);
      
      // Reset form
      setFormData({
        advertisementType: '',
        fullName: '',
        phoneNumber: '',
        email: '',
        targetAudience: ''
      });

    } catch (error) {
      // Error
      setSubmitStatus({
        type: 'error',
        message: 'Something went wrong. Please try again later.'
      });
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle form reset
  const handleReset = () => {
    setFormData({
      advertisementType: '',
      fullName: '',
      phoneNumber: '',
      email: '',
      targetAudience: ''
    });
    setErrors({});
    setSubmitStatus(null);
  };

  // Close success overlay
  const handleCloseOverlay = () => {
    setShowSuccessOverlay(false);
  };

  return (
    <div className="bg-[#FAFAFA] rounded-[24px] py-20 px-4 relative">
      <div className="max-w-[1200px] mx-auto bg-white rounded-[24px] shadow-[0px_7px_24px_rgba(149,157,165,0.12)] p-8 md:p-12">
        
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-[40px] font-semibold text-[#3B3B3B] tracking-[-0.04em] mb-4">
            Send Us Your Advert Request
          </h2>
          <p className="text-[18px] text-[#898989]">
            The CloseCarbon advert team will respond to you within 24 hours.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="max-w-[1000px] mx-auto">
          <div className="space-y-8">
            
          {/* Advertisement Type */}
<div>
  <label htmlFor="advertisementType" className="block text-[18px] font-semibold text-[#141414] mb-3">
    Advertisement type
  </label>
  <select
    id="advertisementType"
    name="advertisementType"
    value={formData.advertisementType}
    onChange={handleInputChange}
    className={`w-full h-[64px] px-10 rounded-[12px] border ${
      errors.advertisementType ? 'border-red-500' : 'border-[#B1B1B1]'
    } focus:outline-none focus:ring-2 focus:ring-[#0D924F] focus:border-transparent p-5 text-[#898989] bg-white text-[14px] appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M6%209L12%2015L18%209%22%20stroke%3D%22%23898989%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E')] bg-[length:24px] bg-[center_right_20px] bg-no-repeat pr-[52px]`}
  >
    <option value="" className="text-[#898989] text-[14px]">Select advertisement type</option>
    <option className='text-[14px] text-[#898989] ' value="banner">Banner advertisement</option>
    <option className='text-[14px] text-[#898989] ' value="sponsored">Sponsored content</option>
    <option className='text-[14px] text-[#898989] ' value="video">Video advertisement</option>
    <option className='text-[14px] text-[#898989] ' value="newsletter">Newsletter promotion</option>
    <option className='text-[14px] text-[#898989] ' value="social">Social media promotion</option>
  </select>
  {errors.advertisementType && (
    <p className="mt-2 text-sm text-red-500">{errors.advertisementType}</p>
  )}
</div>

{/* Target Audience */}
<div>
  <label htmlFor="targetAudience" className="block text-[18px] font-semibold text-[#141414] mb-3">
    Target audience
  </label>
  <select
    id="targetAudience"
    name="targetAudience"
    value={formData.targetAudience}
    onChange={handleInputChange}
    className={`w-full h-[64px] p-5 rounded-[12px] border ${
      errors.targetAudience ? 'border-red-500' : 'border-[#B1B1B1]'
    } focus:outline-none focus:ring-2 focus:ring-[#0D924F] focus:border-transparent text-[#898989] bg-white text-[14px] mb-[32px] appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M6%209L12%2015L18%209%22%20stroke%3D%22%23898989%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E')] bg-[length:24px] bg-[center_right_20px] bg-no-repeat pr-[52px]`}
  >
    <option value="" className="text-[#898989] text-[14px]">Select target audience</option>
    <option className='text-[14px] text-[#898989]' value="all">All users</option>
    <option className='text-[14px] text-[#898989]' value="enterprise">Enterprise organizations</option>
    <option className='text-[14px] text-[#898989]' value="Small & Medium Businesses">Small & Medium Businesses</option>
    <option className='text-[14px] text-[#898989]' value="startup">Startups</option>
    <option className='text-[14px] text-[#898989]' value="investor">Investors</option>
    <option className='text-[14px] text-[#898989]' value="nonprofit">Non-profits</option>
  </select> 
  {errors.targetAudience && (
    <p className="mt-2 text-sm text-red-500">{errors.targetAudience}</p>
  )}
</div>
            {/* Full Name */}
            <div>
              <label htmlFor="fullName" className="block text-[18px] font-semibold text-[#141414] mb-3">
                Full name
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                className={`w-full h-[57px] px-6 rounded-[12px] border ${
                  errors.fullName ? 'border-red-500' : 'border-[#B1B1B1]'
                } focus:outline-none focus:ring-2 focus:ring-[#0D924F] focus:border-transparent text-[#141414] text-[14px]`}
              />
              {errors.fullName && (
                <p className="mt-2 text-sm text-red-500">{errors.fullName}</p>
              )}
            </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="phoneNumber" className="block text-[18px] font-semibold text-[#141414] mb-3">
                Phone number
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                placeholder="Enter your phone number"
                className={`w-full h-[57px] px-6 rounded-[12px] border ${
                  errors.phoneNumber ? 'border-red-500' : 'border-[#B1B1B1] p-6'
                } focus:outline-none focus:ring-2 focus:ring-[#0D924F] focus:border-transparent text-[#141414] text-[14px] p-6`}
              />
              {errors.phoneNumber && (
                <p className="mt-2 text-sm text-red-500">{errors.phoneNumber}</p>
              )}
            </div>

            {/* Email Address */}
            <div>
              <label htmlFor="email" className="block text-[18px] font-semibold text-[#141414] mb-3">
                Email address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                className={`w-full h-[57px] px-6 rounded-[12px] border ${
                  errors.email ? 'border-red-500' : 'border-[#B1B1B1]'
                } focus:outline-none focus:ring-2 focus:ring-[#0D924F] focus:border-transparent text-[#141414] text-[14px]`}
              />
              {errors.email && (
                <p className="mt-2 text-sm text-red-500">{errors.email}</p>
              )}
            </div>

          {/* Target Audience */}
<div>
  <label htmlFor="targetAudience" className="block text-[18px] font-semibold text-[#141414] mb-3">
    Target audience
  </label>
  <select
    id="targetAudience"
    name="targetAudience"
    value={formData.targetAudience}
    onChange={handleInputChange}
    className={`w-full h-[64px] p-5 rounded-[12px] border ${
      errors.targetAudience ? 'border-red-500' : 'border-[#B1B1B1]'
    } focus:outline-none focus:ring-2 focus:ring-[#0D924F] focus:border-transparent text-[#898989] bg-white text-[14px] mb-[32px] appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%3E%3Cpath%20d%3D%22M6%209L12%2015L18%209%22%20stroke%3D%22%23898989%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E')] bg-[length:24px] bg-[center_right_20px] bg-no-repeat pr-[52px]`}
  >
    <option value="" className="text-[#898989] text-[14px]">Select target audience</option>
    <option className='text-[14px] text-[#898989]' value="all">All users</option>
    <option className='text-[14px] text-[#898989]' value="enterprise">Enterprise organizations</option>
    <option className='text-[14px] text-[#898989]' value="Small & Medium Businesses">Small & Medium Businesses</option>
    <option className='text-[14px] text-[#898989]' value="startup">Startups</option>
    <option className='text-[14px] text-[#898989]' value="investor">Investors</option>
    <option className='text-[14px] text-[#898989]' value="nonprofit">Non-profits</option>
  </select> 
  {errors.targetAudience && (
    <p className="mt-2 text-sm text-red-500">{errors.targetAudience}</p>
  )}
</div>
            {/* Error Status */}
            {submitStatus && submitStatus.type === 'error' && (
              <div className="p-4 rounded-lg bg-red-50 text-red-800 border border-red-200">
                {submitStatus.message}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-[77px] bg-[#0D924F] hover:bg-[#0B7A41] disabled:bg-[#0D924F]/70 text-white text-[24px] font-medium rounded-[12px] border-2 border-[#E4E7EC] transition-colors duration-200"
            >
              {isSubmitting ? 'Submitting...' : 'Submit '}
            </button>
          </div>
        </form>
      </div>

      {/* Success Overlay */}
      {showSuccessOverlay && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-[24px] w-full max-w-[800px] relative p-8 md:p-12">
            {/* Close Button */}
            <button
              onClick={handleCloseOverlay}
              className="absolute top-12 right-12 w-6 h-6 flex items-center justify-center hover:opacity-70 transition-opacity"
              aria-label="Close"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="#000000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* Success Icon */}
            <div className="flex justify-center mt-8">
              <div className="w-[200px] h-[200px] relative">
                <svg width="200" height="200" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="100" cy="100" r="83.33" fill="#0D924F"/>
                  <path d="M66.67 100L86.67 120L133.33 73.33" stroke="white" strokeWidth="12" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            {/* Success Content */}
            <div className="text-center mt-6">
              <h3 className="text-[32px] font-semibold text-[#3B3B3B] tracking-[-0.02em] mb-3">
                Request Submitted Successfully
              </h3>
              <p className="text-[20px] text-[#898989] tracking-[-0.02em] max-w-[567px] mx-auto">
                Thank you for submitting your advertisement request. Our partnerships team will reach out to{' '}
                <span className="text-[#3B3B3B] font-medium">{formData.email || 'you'}</span> within 24 hours.
              </p>
            </div>

            {/* Action Button */}
            <div className="flex justify-center mt-8">
              <button
                onClick={handleCloseOverlay}
                className="w-full max-w-[400px] h-[77px] bg-[#0D924F] hover:bg-[#0B7A41] text-white text-[24px] font-medium rounded-[12px] border-2 border-[#E4E7EC] transition-colors duration-200"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdvertRequestForm;